import { FilterOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Popover, Space, Tooltip } from 'antd';
import React, { memo, useCallback, useMemo, useState } from 'react';

interface FilterDropdownProps {
  columnKey: string;
  title: string;
  filterOptions: string[];
  selectedValues: string[];
  onFilterChange: (values: string[]) => void;
  onClear: () => void;
  extraContent?: React.ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}

interface FilterOptionProps {
  option: string;
  isSelected: boolean;
  onChange: (value: string, checked: boolean) => void;
}

const FilterOption = memo(
  ({ option, isSelected, onChange }: FilterOptionProps) => (
    <div className="filter-dropdown__option">
      <Checkbox
        checked={isSelected}
        onChange={(e) => onChange(option, e.target.checked)}
      >
        <span className="filter-dropdown__option-text">{option}</span>
      </Checkbox>
    </div>
  ),
);
FilterOption.displayName = 'FilterOption';

const OPTION_PAGE_SIZE = 100;

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  title,
  filterOptions,
  selectedValues,
  onFilterChange,
  onClear,
  extraContent,
  isActive = false,
  disabled = false,
  disabledTooltip = 'Not available yet',
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [displayLimit, setDisplayLimit] = useState(OPTION_PAGE_SIZE);
  const [tempSelected, setTempSelected] = useState<Set<string>>(
    new Set(selectedValues),
  );

  const filteredOptions = useMemo(() => {
    if (!searchValue) {
      return filterOptions;
    }
    return filterOptions.filter((option) =>
      option.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [filterOptions, searchValue]);

  const visibleOptions = useMemo(
    () => filteredOptions.slice(0, displayLimit),
    [filteredOptions, displayLimit],
  );
  const hasMore = filteredOptions.length > visibleOptions.length;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setTempSelected(new Set(selectedValues));
      setSearchValue('');
      setDisplayLimit(OPTION_PAGE_SIZE);
    }
    setOpen(nextOpen);
  };

  const handleCheckboxChange = useCallback(
    (value: string, checked: boolean) => {
      setTempSelected((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(value);
        } else {
          next.delete(value);
        }
        return next;
      });
    },
    [],
  );

  const handleCheckAllChange = useCallback(
    (checked: boolean) => {
      setTempSelected(new Set(checked ? filteredOptions : []));
    },
    [filteredOptions],
  );

  const handleLoadMore = useCallback(() => {
    setDisplayLimit((prev) => prev + OPTION_PAGE_SIZE);
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
      setDisplayLimit(OPTION_PAGE_SIZE);
    },
    [],
  );

  const handleOk = useCallback(() => {
    onFilterChange(Array.from(tempSelected));
    setOpen(false);
  }, [onFilterChange, tempSelected]);

  const handleClear = useCallback(() => {
    setTempSelected(new Set());
    onClear();
    setOpen(false);
  }, [onClear]);

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const isAllSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => tempSelected.has(option));
  const isIndeterminate =
    filteredOptions.some((option) => tempSelected.has(option)) &&
    !isAllSelected;

  const iconClassName = `filter-dropdown__icon${
    selectedValues.length > 0 || isActive
      ? ' filter-dropdown__icon--active'
      : ''
  }`;

  const content = useMemo(
    () => (
      <div className="filter-dropdown__content">
        <div className="filter-dropdown__search">
          <Input
            placeholder={`Search ${title}...`}
            value={searchValue}
            onChange={handleSearchChange}
            allowClear
            size="small"
          />
        </div>
        {extraContent && (
          <div className="filter-dropdown__extra-content">{extraContent}</div>
        )}
        <div className="filter-dropdown__select-all">
          <Checkbox
            indeterminate={isIndeterminate}
            checked={isAllSelected}
            onChange={(e) => handleCheckAllChange(e.target.checked)}
          >
            Select All ({filteredOptions.length})
          </Checkbox>
        </div>
        <div className="filter-dropdown__options">
          {filteredOptions.length === 0 ? (
            <div className="filter-dropdown__no-options">No options</div>
          ) : (
            visibleOptions.map((option) => (
              <FilterOption
                key={option}
                option={option}
                isSelected={tempSelected.has(option)}
                onChange={handleCheckboxChange}
              />
            ))
          )}
          {hasMore && (
            <div className="filter-dropdown__load-more">
              <Button size="small" type="link" onClick={handleLoadMore}>
                Load more ({filteredOptions.length - visibleOptions.length}{' '}
                remaining)
              </Button>
            </div>
          )}
        </div>
        <div className="filter-dropdown__footer">
          <Space className="filter-dropdown__footer-space">
            <span className="filter-dropdown__footer-count">
              {tempSelected.size} selected
            </span>
            <Space>
              <Button type="link" size="small" onClick={handleClear}>
                Clear
              </Button>
              <Button size="small" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="primary" size="small" onClick={handleOk}>
                OK
              </Button>
            </Space>
          </Space>
        </div>
      </div>
    ),
    [
      filteredOptions,
      handleCheckAllChange,
      handleCheckboxChange,
      handleClear,
      handleCancel,
      handleOk,
      handleLoadMore,
      handleSearchChange,
      hasMore,
      isAllSelected,
      isIndeterminate,
      searchValue,
      tempSelected,
      title,
      visibleOptions,
      extraContent,
    ],
  );

  if (disabled) {
    return (
      <Tooltip title={disabledTooltip}>
        <span
          className="filter-dropdown__icon filter-dropdown__icon--disabled"
          onClick={(e) => e.stopPropagation()}
        >
          <FilterOutlined />
        </span>
      </Tooltip>
    );
  }

  return (
    <Popover
      content={content}
      title={`Filter ${title}`}
      trigger="click"
      placement="bottomLeft"
      open={open}
      onOpenChange={handleOpenChange}
      destroyOnHidden={false}
    >
      <span className={iconClassName} onClick={(e) => e.stopPropagation()}>
        <FilterOutlined />
      </span>
    </Popover>
  );
};

export default React.memo(FilterDropdown);
